import {
  guestUsersApiRef,
  GuestUsersAuth,
  guestUsersAuthApiRef,
  LdapAuth,
  ldapAuthApiRef,
  oidcAuthApiRef,
  userConfigApiRef,
} from '@agilelab/plugin-wb-auth';
import {
  configurationApiRef,
  ConfigurationClient,
} from '@agilelab/plugin-wb-configuration-common';
import {
  DefaultEnvironmentApi,
  environmentApiRef,
} from '@agilelab/plugin-wb-environment';
import { eventsApiRef } from '@agilelab/plugin-wb-events';
import { EventsClient } from '@agilelab/plugin-wb-events-common';
import { extensionManagerApiRef } from '@agilelab/plugin-wb-extension-manager';
import { ExtensionManagerClient } from '@agilelab/plugin-wb-extension-manager-common';
import { GuestUsersAuthClient } from '@agilelab/plugin-wb-guest-users-auth-common';
import {
  auditApiRef,
  CustomAlertApiForwarder,
  customAlertApiRef,
  customFrontendApiRef,
  CustomFrontendClient,
  CustomPresentationRetriever,
  CustomPresentationRetrieverApiRef,
  InlineScriptNunjucksRenderer,
  MeshAuditClient,
  wbSafeNunjucksApiRef,
} from '@agilelab/plugin-wb-platform';
import {
  accessTokenApiRef,
  serviceAccountsApiRef,
} from '@agilelab/backstage-plugin-wb-service-accounts';
import {
  AccessTokenClient,
  ServiceAccountsClient,
} from '@agilelab/plugin-wb-service-accounts-common';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { PracticeShaperClient } from '@agilelab/plugin-wb-practice-shaper-common';
import {
  prototypeApiRef,
  PrototypeClient,
} from '@agilelab/plugin-wb-prototype';
import {
  witboostSearchApiRef,
  WitboostSearchClient,
} from '@agilelab/plugin-wb-search';
import { UserConfigClient } from '@agilelab/plugin-wb-user-config-client';
import { userHeadersApiRef } from '@agilelab/plugin-wb-user-headers';
import { UserHeadersClient } from '@agilelab/plugin-wb-user-headers-common';
import {
  featureRequestRequesterApiRef,
  FeatureRequestRequesterClient,
} from '@agilelab/plugin-wb-user-settings';
import {
  wittyAssistantApiRef,
  WittyAssistantClient,
  wittyAutocompleteApiRef,
  WittyAutocompleteClient,
  wittySkillsApiRef,
  WittySkillsClient,
} from '@agilelab/plugin-wb-witty-react';
import { OAuth2 } from '@mainstage/core-app-api';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
} from '@mainstage/core-plugin-api';
import { ScmAuth, scmIntegrationsApiRef } from '@mainstage/integration-react';
import { createDynamicScmIntegrationsApi } from './dynamicScmIntegrationsApi';

import { SystemPrototypeClient } from '@agilelab/plugin-wb-system-prototype';
import { systemPrototypeApiRef } from '@agilelab/plugin-wb-system-prototype-common';
import {
  authExtensionApiRef,
  AuthExtensionClient,
} from '@agilelab/plugin-wb-auth-extension-common';
import { infoApiRef } from '@agilelab/plugin-wb-platform';
import {
  InfoApiClient,
  parseNunjucks,
} from '@agilelab/plugin-wb-platform-common';
import { metricsApiRef } from '@agilelab/plugin-wb-metrics';
import { DefaultMetricsClient } from '@agilelab/plugin-wb-metrics-common';
import { MarketplaceClient } from '@agilelab/plugin-wb-marketplace-common';
import { marketplaceApiRef } from '@agilelab/plugin-wb-marketplace';
import { startupActionsApiRef } from '@agilelab/plugin-wb-startup-actions';
import { StartupActionsClient } from '@agilelab/plugin-wb-startup-actions-common';
import { teamRolesPlugin } from '@agilelab/backstage-plugin-wb-team-roles';

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: marketplaceApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new MarketplaceClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: metricsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new DefaultMetricsClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      createDynamicScmIntegrationsApi({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: customAlertApiRef,
    deps: {},
    factory: () => new CustomAlertApiForwarder(),
  }),
  createApiFactory({
    api: authExtensionApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new AuthExtensionClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: ldapAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, configApi }) =>
      LdapAuth.create({
        discoveryApi,
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),
  createApiFactory({
    api: guestUsersAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, configApi }) =>
      GuestUsersAuth.create({
        discoveryApi,
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'oidc',
          title: 'Open ID Connect',
          icon: () => null,
        },
        defaultScopes: ['openid', 'profile', 'email'],
      }),
  }),
  createApiFactory({
    api: userConfigApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new UserConfigClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: auditApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ discoveryApi, identityApi }) =>
      new MeshAuditClient(discoveryApi, identityApi),
  }),
  createApiFactory({
    api: userHeadersApiRef,
    deps: {
      userConfigApi: userConfigApiRef,
      configApi: configApiRef,
    },
    factory: ({ userConfigApi, configApi }) =>
      new UserHeadersClient(userConfigApi, {
        enabled: configApi.getBoolean('mesh.userHeaders.enabled'),
        prefix: configApi.getString('mesh.userHeaders.prefix'),
      }),
  }),
  createApiFactory({
    api: practiceShaperApiRef,
    deps: {
      configApi: configApiRef,
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ configApi, discoveryApi, fetchApi }) =>
      new PracticeShaperClient({
        config: configApi,
        discoveryApi,
        fetchApi,
      }),
  }),
  createApiFactory({
    api: configurationApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new ConfigurationClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: extensionManagerApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new ExtensionManagerClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: witboostSearchApiRef,
    deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
    factory: ({ discoveryApi, identityApi }) =>
      new WitboostSearchClient({ discoveryApi, identityApi }),
  }),
  createApiFactory({
    api: eventsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new EventsClient({
        discoveryApi,
        fetchApi,
      }),
  }),
  createApiFactory({
    api: customFrontendApiRef,
    deps: {
      configApi: configApiRef,
      customAlertApi: customAlertApiRef,
    },
    factory: ({ customAlertApi }) => new CustomFrontendClient(customAlertApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: CustomPresentationRetrieverApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new CustomPresentationRetriever({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: wbSafeNunjucksApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => {
      const experimentalNunjucksRenderingEnabled =
        configApi.getOptionalBoolean('mesh.experimentalNunjucksRendering') ??
        true;

      if (experimentalNunjucksRenderingEnabled)
        return new InlineScriptNunjucksRenderer();

      // failsafe mechanism to disable new nunjucks rendering flow if something goes wrong and fallback to the old one (doesn't work without unsafe-eval in csp)
      return {
        load: () => () => void 0,
        render: (template, data) => parseNunjucks(template, data, true),
      };
    },
  }),
  createApiFactory({
    api: prototypeApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new PrototypeClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: environmentApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new DefaultEnvironmentApi({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: wittyAutocompleteApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ fetchApi, discoveryApi }) =>
      new WittyAutocompleteClient(fetchApi, discoveryApi),
  }),
  createApiFactory({
    api: wittyAssistantApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ fetchApi, discoveryApi }) =>
      new WittyAssistantClient(fetchApi, discoveryApi),
  }),
  createApiFactory({
    api: wittySkillsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ fetchApi, discoveryApi }) =>
      new WittySkillsClient(fetchApi, discoveryApi),
  }),
  createApiFactory({
    api: guestUsersApiRef,
    deps: { discoveryApi: discoveryApiRef },
    factory: ({ discoveryApi }) => new GuestUsersAuthClient(discoveryApi),
  }),
  createApiFactory({
    api: startupActionsApiRef,
    deps: { discoveryApi: discoveryApiRef },
    factory: ({ discoveryApi }) => new StartupActionsClient({ discoveryApi }),
  }),
  createApiFactory({
    api: systemPrototypeApiRef,
    deps: { discoveryApi: discoveryApiRef, customAlertApi: customAlertApiRef },
    factory: ({ discoveryApi, customAlertApi }) =>
      new SystemPrototypeClient({ discoveryApi, alertApi: customAlertApi }),
  }),
  createApiFactory({
    api: infoApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new InfoApiClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: serviceAccountsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new ServiceAccountsClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: accessTokenApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new AccessTokenClient({ discoveryApi, fetchApi }),
  }),
  ...teamRolesPlugin.getApis(),
  createApiFactory({
    api: featureRequestRequesterApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new FeatureRequestRequesterClient({ discoveryApi, fetchApi }),
  }),
];
